import { useCallback, useEffect, useRef, useState } from 'react';
import { Building2, CheckCircle2, ChevronDown, ChevronLeft, ChevronRight, ChevronsUpDown, ChevronUp, GraduationCap, Loader2, Search, ShieldCheck, X } from 'lucide-react';
import { toast } from 'react-toastify';
import Sidebar from '../components/layout/Sidebar';
import {
  achievementService,
  type AdminAchievementListItem,
  type AdminOrganizationItem,
  type AdminOrganizationSortBy,
  type ListAdminOrganizationsParams,
  type OrganizationStudentItem,
  type PaginationMeta,
} from '../services/achievementService';

// Which drill-down a clicked count opens.
type DrillView = 'achievements' | 'students' | 'verified';

function studentName(s: { firstName: string | null; lastName: string | null; email: string }): string {
  const composed = [s.firstName, s.lastName].filter(Boolean).join(' ').trim();
  return composed.length > 0 ? composed : s.email;
}

const PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 300;

const COLUMNS: ReadonlyArray<{ label: string; sortKey: AdminOrganizationSortBy; align?: 'center' }> = [
  { label: 'Organization', sortKey: 'organization' },
  { label: 'Achievements', sortKey: 'achievementCount', align: 'center' },
  { label: 'Students', sortKey: 'studentCount', align: 'center' },
  { label: 'Verified', sortKey: 'verifiedCount', align: 'center' },
];

export default function Organizations() {
  const [qInput, setQInput] = useState('');
  const [q, setQ] = useState('');
  const [sortBy, setSortBy] = useState<AdminOrganizationSortBy>('achievementCount');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState<AdminOrganizationItem[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  // The open drill-down modal (a clicked count cell), or null.
  const [drill, setDrill] = useState<{ organization: string; view: DrillView } | null>(null);
  const fetchSeqRef = useRef(0);

  // Debounce the search box; reset to page 1 when the query changes.
  useEffect(() => {
    const t = setTimeout(() => {
      setQ(qInput.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [qInput]);

  const fetchOrganizations = useCallback(async () => {
    const seq = ++fetchSeqRef.current;
    setIsLoading(true);
    try {
      const params: ListAdminOrganizationsParams = { page, pageSize: PAGE_SIZE, sortBy, sortDir };
      if (q) params.q = q;
      const response = await achievementService.listAdminOrganizations(params);
      if (fetchSeqRef.current !== seq) return;
      setRows(response.data);
      setTotal(response.meta.total);
      setTotalPages(response.meta.totalPages);
    } catch (err: unknown) {
      if (fetchSeqRef.current !== seq) return;
      toast.error(err instanceof Error ? err.message : 'Failed to load organizations');
      setRows([]);
      setTotal(0);
      setTotalPages(1);
    } finally {
      if (fetchSeqRef.current === seq) setIsLoading(false);
    }
  }, [page, q, sortBy, sortDir]);

  useEffect(() => {
    fetchOrganizations();
  }, [fetchOrganizations]);

  const toggleSort = (key: AdminOrganizationSortBy) => {
    if (sortBy === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(key);
      // Counts read best high→low; the name reads best A→Z.
      setSortDir(key === 'organization' ? 'asc' : 'desc');
    }
    setPage(1);
  };

  return (
    <div className="bg-white min-h-screen font-manrope pb-[100px]">
      <Sidebar />

      <div className="ml-[230px] mt-6 px-8 flex flex-col gap-6">
        <div className="flex flex-col gap-0.5">
          <h2 className="text-gray-900 text-2xl font-bold">Organizations</h2>
          <p className="text-gray-500 text-base font-normal">
            Award-providing organizations across all students — how many achievements and students each is attached to, and how many are verified.
          </p>
        </div>

        {/* ── Search ───────────────────────────────────────────────────── */}
        <div className="relative max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          <input
            type="text"
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            placeholder="Search organizations…"
            className="w-full pl-9 pr-9 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          {qInput && (
            <button
              type="button"
              onClick={() => setQInput('')}
              aria-label="Clear search"
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
            >
              <X size={14} />
            </button>
          )}
        </div>

        {/* ── Table ────────────────────────────────────────────────────── */}
        <div className="border border-blue-100 rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-blue-50 text-gray-700">
                <tr className="text-left text-xs font-semibold uppercase tracking-wide">
                  {COLUMNS.map((col) => {
                    const isActive = sortBy === col.sortKey;
                    const centered = col.align === 'center';
                    return (
                      <th key={col.label} className={`px-6 py-3 ${centered ? 'text-center' : ''}`}>
                        <button
                          type="button"
                          onClick={() => toggleSort(col.sortKey)}
                          aria-label={`Sort by ${col.label}`}
                          aria-sort={isActive ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
                          className={`group inline-flex items-center gap-1 uppercase tracking-wide hover:text-blue-700 transition-colors ${
                            isActive ? 'text-blue-700' : ''
                          } ${centered ? 'justify-center' : ''}`}
                        >
                          {col.label}
                          {isActive ? (
                            sortDir === 'asc' ? <ChevronUp size={12} className="shrink-0" /> : <ChevronDown size={12} className="shrink-0" />
                          ) : (
                            <ChevronsUpDown size={12} className="shrink-0 text-gray-300 group-hover:text-blue-400" />
                          )}
                        </button>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody className="divide-y divide-blue-100">
                {isLoading && rows.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-6 py-12 text-center">
                      <Loader2 className="inline-block w-5 h-5 animate-spin text-gray-400" />
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-6 py-12 text-center text-gray-500">
                      {q ? 'No organizations match your search.' : 'No organizations yet.'}
                    </td>
                  </tr>
                ) : (
                  rows.map((row) => (
                    <tr key={row.organization} className="hover:bg-blue-50/40">
                      <td className="px-6 py-3 text-gray-900 font-medium">
                        <span className="inline-flex items-center gap-2">
                          <Building2 size={15} className="shrink-0 text-gray-400" />
                          {row.organization}
                        </span>
                      </td>
                      <td className="px-6 py-3 text-center">
                        <CountButton
                          value={row.achievementCount}
                          label={`View achievements for ${row.organization}`}
                          onClick={() => setDrill({ organization: row.organization, view: 'achievements' })}
                        />
                      </td>
                      <td className="px-6 py-3 text-center">
                        <CountButton
                          value={row.studentCount}
                          label={`View students for ${row.organization}`}
                          onClick={() => setDrill({ organization: row.organization, view: 'students' })}
                        />
                      </td>
                      <td className="px-6 py-3 text-center">
                        {row.verifiedCount > 0 ? (
                          <button
                            type="button"
                            onClick={() => setDrill({ organization: row.organization, view: 'verified' })}
                            aria-label={`View verified achievements for ${row.organization}`}
                            className="inline-flex items-center justify-center min-w-[28px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-xs font-semibold hover:bg-emerald-200 focus:outline-none focus:ring-2 focus:ring-emerald-400 transition-colors"
                          >
                            {row.verifiedCount}
                          </button>
                        ) : (
                          <span className="text-gray-300">0</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* ── Pagination ───────────────────────────────────────────────── */}
        <div className="flex justify-between items-center">
          <span className="text-xs text-gray-500">
            {total === 0 ? 'No results' : `Showing ${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, total)} of ${total.toLocaleString()}`}
          </span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1 || isLoading}
              className="p-2 rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              aria-label="Previous page"
            >
              <ChevronLeft size={16} />
            </button>
            <span className="text-xs text-gray-700 px-3">
              Page {page} of {totalPages}
            </span>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages || isLoading}
              className="p-2 rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              aria-label="Next page"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </div>

      {drill && (
        <DrillDownModal
          key={`${drill.organization}::${drill.view}`}
          organization={drill.organization}
          view={drill.view}
          onClose={() => setDrill(null)}
        />
      )}
    </div>
  );
}

// A count cell that opens a drill-down. Zero renders as an inert muted "0"
// (nothing to list).
function CountButton({ value, label, onClick }: { value: number; label: string; onClick: () => void }) {
  if (value === 0) return <span className="text-gray-300">0</span>;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className="text-blue-700 font-medium hover:underline underline-offset-2 focus:outline-none focus:ring-2 focus:ring-blue-400 rounded px-1"
    >
      {value.toLocaleString()}
    </button>
  );
}

const DRILL_PAGE_SIZE = 25;

const DRILL_TITLE: Record<DrillView, string> = {
  achievements: 'Achievements',
  students: 'Students',
  verified: 'Verified achievements',
};

function DrillDownModal({ organization, view, onClose }: { organization: string; view: DrillView; onClose: () => void }) {
  const [page, setPage] = useState(1);
  const [achievements, setAchievements] = useState<AdminAchievementListItem[]>([]);
  const [students, setStudents] = useState<OrganizationStudentItem[]>([]);
  const [meta, setMeta] = useState<PaginationMeta | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const isStudents = view === 'students';

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    const request = isStudents
      ? achievementService.listOrganizationStudents({ organization, page, pageSize: DRILL_PAGE_SIZE })
      : achievementService.listOrganizationAchievements({ organization, verifiedOnly: view === 'verified', page, pageSize: DRILL_PAGE_SIZE });
    request
      .then((res) => {
        if (cancelled) return;
        if (isStudents) setStudents((res as { data: OrganizationStudentItem[] }).data);
        else setAchievements((res as { data: AdminAchievementListItem[] }).data);
        setMeta(res.meta);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [organization, view, page, isStudents]);

  const isEmpty = !isLoading && !error && (isStudents ? students.length === 0 : achievements.length === 0);

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-start justify-between p-6 pb-4 border-b border-gray-100">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              {view === 'verified' ? (
                <ShieldCheck className="w-4 h-4 text-emerald-600 shrink-0" />
              ) : isStudents ? (
                <GraduationCap className="w-4 h-4 text-blue-600 shrink-0" />
              ) : (
                <Building2 className="w-4 h-4 text-gray-500 shrink-0" />
              )}
              <h2 className="text-lg font-bold text-gray-900">{DRILL_TITLE[view]}</h2>
              {meta && <span className="text-sm text-gray-400 font-normal">({meta.total.toLocaleString()})</span>}
            </div>
            <p className="text-sm text-gray-500 mt-0.5 truncate">{organization}</p>
          </div>
          <button onClick={onClose} aria-label="Close" className="text-gray-400 hover:text-gray-600 shrink-0">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
            </div>
          ) : error ? (
            <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>
          ) : isEmpty ? (
            <p className="text-sm text-gray-400 italic py-10 text-center">Nothing to show.</p>
          ) : isStudents ? (
            <ul className="divide-y divide-gray-100 border border-gray-100 rounded-xl overflow-hidden">
              {students.map((s) => (
                <li key={s.id} className="px-3 py-2.5 flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center shrink-0 text-xs font-semibold text-gray-500 uppercase">
                    {studentName(s).slice(0, 1)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900 truncate">{studentName(s)}</p>
                    <p className="text-xs text-gray-500 truncate">
                      {s.email}
                      {s.schoolName ? ` · ${s.schoolName}` : ''}
                    </p>
                  </div>
                  <span className="shrink-0 text-xs text-gray-500 px-1.5 py-0.5 rounded bg-gray-100">
                    {s.achievementCount.toLocaleString()} {s.achievementCount === 1 ? 'achievement' : 'achievements'}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <ul className="divide-y divide-gray-100 border border-gray-100 rounded-xl overflow-hidden">
              {achievements.map((a) => (
                <li key={a.id} className="px-3 py-2.5 flex items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-gray-900 truncate">{a.title}</p>
                      {a.verified && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-100 text-emerald-800 shrink-0">
                          <CheckCircle2 className="w-3 h-3" />
                          Verified
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 truncate">
                      {studentName({ firstName: a.studentFirstName, lastName: a.studentLastName, email: a.studentEmail })}
                      {a.schoolName ? ` · ${a.schoolName}` : ''}
                    </p>
                  </div>
                  <span className="shrink-0 text-[10px] text-gray-500 uppercase tracking-wide px-1.5 py-0.5 rounded bg-gray-100">{a.achievementType}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Footer pagination */}
        {meta && meta.totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
            <span className="text-xs text-gray-500">
              Page {meta.page} of {meta.totalPages}
            </span>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1 || isLoading}
                className="p-1.5 rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                aria-label="Previous page"
              >
                <ChevronLeft size={16} />
              </button>
              <button
                type="button"
                onClick={() => setPage((p) => (meta ? Math.min(meta.totalPages, p + 1) : p + 1))}
                disabled={page >= meta.totalPages || isLoading}
                className="p-1.5 rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                aria-label="Next page"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
